/**
 * Получатели конкретной ручной рассылки (с диаграммой)
 * Дата: 26.02.2026 — PLAN Manual Broadcasts, Итерация 6
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  SearchInput,
  EmptyState,
  LoadingState,
  Pagination,
  PageSizeSelect,
  FilterSelect,
} from '@/components/ui';
import { manualBroadcastsApi, type BroadcastRecipient } from '@/api/manualBroadcasts';
import { formatPhone } from '@/utils/formatters';
import { formatDateTime } from '@/utils/formatters';
import { RecipientStatusBadge } from '@/components/features/manual-broadcasts/RecipientStatusBadge';
import { RecipientSelector } from '@/components/features/manual-broadcasts/RecipientSelector';
import { useToast } from '@/hooks/useToast';

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'sent', label: 'Отправлено' },
  { value: 'failed', label: 'Ошибка' },
  { value: 'skipped', label: 'Пропущено' },
];

export function ManualBroadcastRecipientsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const tenantIdFromQuery = new URLSearchParams(location.search).get('tenant_id') || undefined;
  const [broadcastName, setBroadcastName] = useState('');
  const [broadcastStatus, setBroadcastStatus] = useState('');
  const [broadcastSegmentType, setBroadcastSegmentType] = useState<'regular' | 'dynamic' | 'lk'>('regular');
  const [broadcastSegmentId, setBroadcastSegmentId] = useState(0);
  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);
  const [previewError, setPreviewError] = useState('');
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [draftSelectedPhones, setDraftSelectedPhones] = useState<Set<string>>(new Set());
  const [draftSelectionDirty, setDraftSelectionDirty] = useState(false);
  const [savingDraftSelection, setSavingDraftSelection] = useState(false);
  const draftSelectionInitializedRef = useRef(false);
  const skipNextDraftDirtyRef = useRef(false);

  useEffect(() => {
    draftSelectionInitializedRef.current = false;
    skipNextDraftDirtyRef.current = false;
    setDraftSelectedPhones(new Set());
    setDraftSelectionDirty(false);
  }, [id]);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setPreviewError('');
    try {
      const [broadcastRes, recipientsRes] = await Promise.all([
        manualBroadcastsApi.get(id, tenantIdFromQuery),
        manualBroadcastsApi.listRecipients(id, {
          search: search || undefined,
          status: statusFilter || undefined,
          limit: pageSize,
          offset: page * pageSize,
          tenant_id: tenantIdFromQuery,
        }),
      ]);
      setBroadcastName(broadcastRes.name);
      setBroadcastStatus(broadcastRes.status || '');
      setBroadcastSegmentType(broadcastRes.segment_type || 'regular');
      setBroadcastSegmentId(broadcastRes.segment_id || 0);

      if (broadcastRes.status === 'draft') {
        if (!draftSelectionInitializedRef.current) {
          skipNextDraftDirtyRef.current = true;
          setDraftSelectedPhones(new Set(broadcastRes.selected_phones ?? []));
          draftSelectionInitializedRef.current = true;
        }
        setRecipients([]);
        setTotal(0);
        setPreviewError('');
        return;
      }

      setRecipients(recipientsRes.recipients);
      setPreviewError('');
      setTotal(recipientsRes.total);
    } catch (e: any) {
      setRecipients([]);
      setBroadcastStatus('');
      setPreviewError(e?.response?.data?.error || 'Не удалось загрузить предпросмотр получателей');
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [id, page, pageSize, statusFilter, search, tenantIdFromQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = () => {
    setPage(0);
    load();
  };

  const handleDraftSelectionChange = (phones: Set<string>) => {
    setDraftSelectedPhones(phones);
    if (skipNextDraftDirtyRef.current) {
      skipNextDraftDirtyRef.current = false;
      return;
    }
    setDraftSelectionDirty(true);
  };

  const handleSaveDraftRecipients = async () => {
    if (!id) return;
    if (draftSelectedPhones.size === 0) {
      showError('Нужно оставить хотя бы одного получателя');
      return;
    }
    try {
      setSavingDraftSelection(true);
      await manualBroadcastsApi.update(
        id,
        { selected_phones: Array.from(draftSelectedPhones) },
        tenantIdFromQuery,
      );
      setDraftSelectionDirty(false);
      showSuccess('Состав получателей сохранён');
      await load();
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось сохранить состав получателей');
    } finally {
      setSavingDraftSelection(false);
    }
  };

  const handleResetDraftRecipients = async () => {
    draftSelectionInitializedRef.current = false;
    skipNextDraftDirtyRef.current = false;
    setDraftSelectionDirty(false);
    await load();
  };

  const sent = (recipients ?? []).filter((r) => r.status === 'sent').length;
  const failed = (recipients ?? []).filter((r) => r.status === 'failed').length;
  const skipped = (recipients ?? []).filter((r) => r.status === 'skipped').length;
  const totalPage = sent + failed + skipped;
  const isDraftMode = broadcastStatus === 'draft';

  return (
    <MainLayout
      headerTitle={`Получатели: ${broadcastName || '...'}`}
      headerActions={
        <>
          <button
            onClick={() => navigate('/manual-broadcasts')}
            className="flex items-center gap-2 px-3 py-2 text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="w-4 h-4" />
            К списку
          </button>
          <button
            onClick={() => load()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-neutral-600 hover:text-neutral-900 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        {id && (
          <>
            {!isDraftMode && (
              <div className="flex gap-4 items-end">
                <div className="w-48">
                  <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder="Телефон или имя"
                  />
                </div>
                <div className="w-40">
                  <FilterSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={STATUS_OPTIONS}
                    placeholder="Статус"
                  />
                </div>
                <PageSizeSelect value={pageSize} onChange={setPageSize} />
                <button
                  onClick={handleApply}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  Применить
                </button>
              </div>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{isDraftMode ? 'Состав черновика' : `Получатели (${total})`}</CardTitle>
                {totalPage > 0 && (
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">Отправлено: {sent}</span>
                    <span className="text-red-600">Ошибка: {failed}</span>
                    <span className="text-amber-600">Пропущено: {skipped}</span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loading ? (
                  <LoadingState />
                ) : isDraftMode ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                      Здесь можно заранее подготовить кампанию по сегменту и снять лишние чекбоксы.
                      Пока суперадмин не включит запуск ручных рассылок, коллега не сможет нажать `Запустить`.
                    </div>
                    <RecipientSelector
                      segmentType={broadcastSegmentType}
                      segmentId={broadcastSegmentId}
                      tenantId={tenantIdFromQuery}
                      selectedPhones={draftSelectedPhones}
                      onSelectionChange={handleDraftSelectionChange}
                      previewRequestFactory={(patient) => {
                        if (!id) return null;
                        return {
                          phone: patient.phone,
                          broadcast_id: id,
                        };
                      }}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm text-neutral-500">
                        {draftSelectionDirty
                          ? 'Есть несохранённые изменения состава получателей.'
                          : `Сохранено получателей: ${draftSelectedPhones.size}`}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={handleResetDraftRecipients}
                          disabled={savingDraftSelection}
                        >
                          Сбросить
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleSaveDraftRecipients}
                          disabled={savingDraftSelection || draftSelectedPhones.size === 0 || !draftSelectionDirty}
                        >
                          {savingDraftSelection ? 'Сохранение…' : 'Сохранить состав'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : !recipients || recipients.length === 0 ? (
                  <EmptyState
                    type="noData"
                    title="Нет получателей"
                    description={previewError || 'Получатели появятся после запуска рассылки'}
                  />
                ) : (
                  <>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Телефон</TableHeader>
                          <TableHeader>Имя</TableHeader>
                          <TableHeader>Статус</TableHeader>
                          <TableHeader>Канал</TableHeader>
                          <TableHeader>Причина</TableHeader>
                          <TableHeader>Дата</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recipients.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="font-mono text-sm">{formatPhone(r.phone)}</TableCell>
                            <TableCell>{r.patient_name || '—'}</TableCell>
                            <TableCell>
                              <RecipientStatusBadge status={r.status} />
                            </TableCell>
                            <TableCell>{r.channel || '—'}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              <span title={r.skip_reason || r.error_message}>
                                {r.skip_reason || r.error_message || '—'}
                              </span>
                            </TableCell>
                            <TableCell>{formatDateTime(r.sent_at || r.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {total > pageSize && (
                      <Pagination
                        offset={page * pageSize}
                        limit={pageSize}
                        total={total}
                        onOffsetChange={(off) => setPage(Math.floor(off / pageSize))}
                        showPageNumbers
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </MainLayout>
  );
}
